
import { getUserCapabilities } from '../api.js'
import notifications from '../notifications.js'

import changeView from '../capabilities/changeView.js'
import editStudent from '../capabilities/editStudent.js'
import editDriver from '../capabilities/editDriver.js'
import activateDriver from '../capabilities/activateDriver.js'
import validateDriver from '../capabilities/validateDriver.js'
import submitNewStudent from '../capabilities/submitNewStudent.js'
import submitNewStudentPdf from '../capabilities/submitNewStudentPdf.js'
import submitNewDriver from '../capabilities/submitNewDriver.js'
import viewNewRequestForm from '../capabilities/viewNewRequestForm.js'
import viewRouteList from '../capabilities/viewRouteList.js'
import viewStudent from '../capabilities/viewStudent.js'
import viewStudentList from '../capabilities/viewStudentList.js'
import viewVendorList from '../capabilities/viewVendorList.js'
import viewInternalCstData from '../capabilities/viewInternalCstData.js'
import viewVehicleInfo from '../capabilities/viewVehicleInfo.js'
import viewDailyNoShowForm from '../capabilities/viewDailyNoShowForm.js'
import viewLateRouteForm from '../capabilities/viewLateRouteForm.js'
import submitDailyNoShowForm from '../capabilities/submitDailyNoShowForm.js'
import submitLateRouteForm from '../capabilities/submitLateRouteForm.js'
import selectDistrict from '../capabilities/selectDistrict.js'
import selectRequestingDistrict from '../capabilities/selectRequestingDistrict.js'
import selectDriverVendor from '../capabilities/selectDriverVendor.js'
import submitOneTimeNoRide from '../capabilities/submitOneTimeNoRide.js'
import submitOneTimeRide from '../capabilities/submitOneTimeRide.js'
import viewBuilding from '../capabilities/viewBuilding.js'
import viewDistrict from '../capabilities/viewDistrict.js'
import viewRoute from '../capabilities/viewRoute.js'
import viewRouteMap from '../capabilities/viewRouteMap.js'
import viewRouteStopTimes from '../capabilities/viewRouteStopTimes.js'
import viewDriverList from '../capabilities/viewDriverList.js'
import viewDriverForm from '../capabilities/viewDriverForm.js'
import viewVehicleList from '../capabilities/viewVehicleList.js'
import viewVehicleForm from '../capabilities/viewVehicleForm.js'
import submitNewVehicle from '../capabilities/submitNewVehicle.js'
import editVehicle from '../capabilities/editVehicle.js'
import selectVehicleVendor from '../capabilities/selectVehicleVendor.js'
import viewDailyNoShowList from '../capabilities/viewDailyNoShowList.js'
import viewOneTimeNoRideList from '../capabilities/viewOneTimeNoRideList.js'
import viewOneTimeRide from '../capabilities/viewOneTimeRide.js'
import viewParentOwnStudent from '../capabilities/viewParentOwnStudent.js'
import viewStudentNotification from '../capabilities/viewStudentNotification.js'
import viewUserManagement from '../capabilities/viewUserManagement.js'
import viewStudentSearch from '../capabilities/viewStudentSearch.js'
import viewStudentStops from '../capabilities/viewStudentStops.js'
import viewStudentMap from '../capabilities/viewStudentMap.js'
import submitUserManagement from '../capabilities/submitUserManagement.js'
import viewStudentNotes from '../capabilities/viewStudentNotes.js'
import editStudentNotes from '../capabilities/editStudentNotes.js'
import editStudentContact from '../capabilities/editStudentContact.js'
import submitStudentNotes from '../capabilities/submitStudentNotes.js'
import viewVendorForms from '../capabilities/viewVendorForms.js'
import viewVendor from '../capabilities/viewVendor.js'
import submitStudentNotification from '../capabilities/submitStudentNotification.js'
import skipEmailGeneration from '../capabilities/skipEmailGeneration.js'
import cancelStudent from '../capabilities/cancelStudent.js'
import submitCalendarEvent from '../capabilities/submitCalendarEvent.js'
import viewEventList from '../capabilities/viewEventList.js'
import viewCalendarEvent from '../capabilities/viewCalendarEvent.js'
import filterRouteOnError from '../capabilities/filterRouteOnError.js'
import showPreviousRouteStatus from '../capabilities/showPreviousRouteStatus.js'

const CAPABILITY_MODULES = [
  changeView,
  editStudent,
  editDriver,
  activateDriver,
  validateDriver,
  submitNewStudent,
  submitNewStudentPdf,
  submitNewDriver,
  viewNewRequestForm,
  viewRouteList,
  viewStudent,
  viewStudentList,
  viewVendorList,
  viewInternalCstData,
  viewVehicleInfo,
  viewDailyNoShowForm,
  viewLateRouteForm,
  submitDailyNoShowForm,
  submitLateRouteForm,
  selectDistrict,
  selectRequestingDistrict,
  selectDriverVendor,
  submitOneTimeNoRide,
  submitOneTimeRide,
  viewBuilding,
  viewDistrict,
  viewRoute,
  viewRouteMap,
  viewRouteStopTimes,
  viewDriverList,
  viewDriverForm,
  viewVehicleList,
  viewVehicleForm,
  submitNewVehicle,
  editVehicle,
  selectVehicleVendor,
  viewDailyNoShowList,
  viewOneTimeNoRideList,
  viewOneTimeRide,
  viewParentOwnStudent,
  viewStudentNotification,
  viewUserManagement,
  submitUserManagement,
  viewStudentMap,
  viewStudentSearch,
  viewStudentStops,
  viewStudentNotes,
  editStudentNotes,
  editStudentContact,
  submitStudentNotes,
  viewVendorForms,
  viewVendor,
  submitStudentNotification,
  skipEmailGeneration,
  cancelStudent,
  submitCalendarEvent,
  viewEventList,
  viewCalendarEvent,
  filterRouteOnError,
  showPreviousRouteStatus,
].reduce(function (accumulator, curr) {
  accumulator[curr.key()] = curr.create
  return accumulator
}, {})

var _vars = {
  capabilityPromise: undefined,
  apiLock: false,
  apiLockReleaseCallback: undefined,
}

function _createPromiseForApiReleaseCallback() {
  return new Promise((resolve) => {
    _vars.apiLockReleaseCallback = function (promiseContent) {
      resolve(promiseContent)
    }
  })
}

function _createPromiseForApiCall() {
  return getUserCapabilities()
    .then(response => {
      if (response.status !== 200) {
        throw new Error('unable to load user capabilities')
      }
      return new Capabilities(response.data)
    })
    .catch(error => {
      console.error(error)
      notifications.pushError('Unable to load user capabilities.', error)
      _vars.capabilityPromise = undefined
    })
}

class Capabilities {
  constructor(capabilityData) {
    this.data = capabilityData
    this.capabilityMap = {}

    for (const capabilityKey in capabilityData) {
      if (CAPABILITY_MODULES[capabilityKey] === undefined) {
        console.error(`Capability with key, ${capabilityKey}, does not have an accompanying file.`)
        continue
      }
      let config = capabilityData[capabilityKey]
      this.capabilityMap[capabilityKey] = CAPABILITY_MODULES[capabilityKey](config)
    }
  }

  checkPermission(capabilityKey, options) {
    let capability = this.capabilityMap[capabilityKey]
    if (capability === undefined) {
      return false
    }
    return capability.checkPermission(options)
  }

  get(capabilityKey) {
    return this.capabilityMap[capabilityKey]
  }
}

function get() {
  if (_vars.apiLock) {
    if (!_vars.capabilityPromise) {
      _vars.capabilityPromise =_createPromiseForApiReleaseCallback()
    }
  }
  else {
    if (!_vars.capabilityPromise) {
      _vars.capabilityPromise = _createPromiseForApiCall()
    }
  }
  return _vars.capabilityPromise
}

function populate(capabilityData) {
  let updatedContent = new Capabilities(capabilityData)

  if (_vars.apiLock) {
    _vars.apiLockReleaseCallback(updatedContent)
    _vars.apiLock = false
  }
  else {
    _vars.capabilityPromise = Promise.resolve(updatedContent)
  }

  return updatedContent
}

function apiLock(boolValue) {
  _vars.apiLock = boolValue

  if (_vars.apiLock && _vars.capabilityPromise === undefined) {
    _vars.capabilityPromise = _createPromiseForApiReleaseCallback()
  }
}

export default {
  get,
  populate,
  apiLock,
}
